import { FormattedHTMLMessage } from "react-intl";

import {
  FormFooterComponent,
  getPrivacyPolicyUrl,
  LinkExternal,
  type VerificationService,
} from "@/es6";
import { makeFormattedHTMLTagReplacer } from "@/lib/utils/customTagReplacer";

import { type SSOContentProps } from "../SingleSignOnComponent";
import { AnnouncingHeader } from "../AnnouncingHeader/AnnouncingHeader";
import { LogoComponent } from "../LogoComponent/LogoComponent";

const ReclaimFooterComponent = ({
  verificationService,
}: {
  verificationService: VerificationService;
}) => (
  <div className="sid-footer__text--transparent">
    <div>
      <b>
        <FormattedHTMLMessage
          id="step.reclaim.legal.thirdPartyTools"
          defaultMessage="The SheerID Verifier uses third-party tools"
        />
      </b>
      :{" "}
      <FormattedHTMLMessage
        id="step.reclaim.legal.thirdPartyToolsExplanation"
        defaultMessage="SheerID uses third-party tools to verify you. By proceeding, you agree to the use of these tools and to share your data with SheerID and its service providers to verify you."
      />
    </div>
    <div className="sid-l-space-top-sm">
      <b>
        <FormattedHTMLMessage id="step.reclaim.legal.secureLogin" defaultMessage="Secure login" />
      </b>
      :{" "}
      <FormattedHTMLMessage
        id="step.reclaim.legal.secureLoginExplanation"
        defaultMessage="We keep your login details secure. SheerID and its service providers will not save or store them."
      />
    </div>
    <div className="sid-l-space-top-sm">
      <b>
        <FormattedHTMLMessage
          id="step.reclaim.legal.consent"
          defaultMessage="You consent and agree"
        />
      </b>
      :{" "}
      <FormattedHTMLMessage
        id="step.reclaim.legal.consentExplanation"
        defaultMessage="By proceeding, you agree to use your own valid login credentials. You agree that your institution allows you to use third-party tools to access and collect information from its website. You consent to SheerID and its service providers collecting personal information from the website to verify you."
      />
    </div>
    <div className="sid-l-space-top-sm">
      <b>
        <FormattedHTMLMessage
          id="step.reclaim.legal.moreInformation"
          defaultMessage="More information"
        />
      </b>
      :{" "}
      <FormattedHTMLMessage
        id="step.reclaim.legal.moreInformationExplanation"
        defaultMessage="To learn more about our privacy practices, including how we use service providers like SheerID and other partners to collect, use, store, and disclose you personal information, see our <privacyPolicy>privacy policy</privacyPolicy>."
      >
        {makeFormattedHTMLTagReplacer({
          // eslint-disable-next-line react/no-unstable-nested-components
          privacyPolicy: (content) => {
            const url = getPrivacyPolicyUrl(verificationService!.programTheme);
            return url ? (
              <LinkExternal className="sid-privacy-policy sid-link" href={url}>
                {content}
              </LinkExternal>
            ) : (
              content
            );
          },
        })}
      </FormattedHTMLMessage>
    </div>
  </div>
);

const ReclaimContentComponent = ({
  messages,
  verificationService,
  updateViewModelAndOpenLoginTab,
  cancelSingleSignOn,
  dashedStepName,
}: SSOContentProps) => (
  <div className="sid-header">
    <div className={`sid-${dashedStepName}-logo-container sid-l-horz-center`}>
      <LogoComponent
        verificationService={verificationService}
        fallbackLogo={
          <img
            className={`sid-${dashedStepName}-logo-default sid-l-lead-image`}
            alt=""
            src="https://assets-resources.sheerid.com/common/images/2018/icons/sso-step.svg"
          />
        }
      />
    </div>
    <AnnouncingHeader align="center">{messages.title}</AnnouncingHeader>
    <div className="sid-header__subtitle-one">{messages.subtitle}</div>
    <FormFooterComponent verificationService={verificationService} showShield>
      <ReclaimFooterComponent verificationService={verificationService} />
    </FormFooterComponent>
    <div
      className={`sid-form-region sid-${dashedStepName}-submit sid-submit-wrapper sid-l-space-top-md`}
    >
      <button
        id={`sid-submit-btn-${dashedStepName}`}
        onClick={() => updateViewModelAndOpenLoginTab("isSingleSignOnActive", true)}
        type="submit"
        className={`sid-btn sid-${dashedStepName}-submit-btn sid-btn--dark sid-l-full-width`}
        aria-label="submit"
      >
        {messages.login}
      </button>
    </div>
    <div
      className={`sid-form-region sid-${dashedStepName}-submit sid-submit-wrapper sid-l-space-top-sm`}
    >
      <button
        id={`sid-cancel-btn-${dashedStepName}`}
        onClick={cancelSingleSignOn}
        type="button"
        className={`sid-btn sid-${dashedStepName}-pending-submit-btn sid-btn-light sid-l-full-width`}
        aria-label="cancel"
      >
        {messages.cancel}
      </button>
    </div>
    <div className="sid-l-space-top-lg" />
  </div>
);
export { ReclaimContentComponent };
